import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiAccordion-root:before': {
      'background-color': 'transparent'
    }
  },
  panel: {
    marginTop: 16,
    marginBottom: 16,
    border: '2px solid #F1F4F5',
    borderRadius: 4,
    boxShadow: 'none'
  },
  paragraph: {
    marginBottom: 16
  },
  faqBubble: {
    marginRight: 16,
    backgroundColor: '#E1F2F2',
    borderRadius: '50%',
    color: '#28939C',
    minWidth: '32px',
    width: '32px',
    height: '32px',
    textAlign: 'center',
    '& span': {
      fontWeight: 'bold',
      lineHeight: '32px'
    }
  },
  list: {
    color: theme.palette.text.secondary
  }
}));

const AccountingFaq = () => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <Accordion className={classes.panel}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          aria-controls="faq-taxes-content"
          id="faq-taxes-header"
        >
          <div className={classes.faqBubble}>
            <Typography component="span" variant="body1">?</Typography>
          </div>
          <Typography variant="h4">What about taxes?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <Typography variant="body1" color="textSecondary" className={classes.paragraph}>
              If your net earnings from self-employment are $400 or more you are responsible for
              paying quarterly self-employment taxes. The SE tax rate on net earnings is 15.3%
              (12.4% social security tax plus 2.9% Medicare tax).
            </Typography>
            <Typography variant="body1" color="textSecondary" className={classes.paragraph}>
              In addition, you must make estimated tax payments for the current tax year if both
              of the following apply:
            </Typography>
            <ol className={classes.list}>
              <li>
                <Typography variant="body1" color="textSecondary">
                  You expect to owe at least $1,000 in tax for the current tax year
                  after subtracting your withholding and refundable credits.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  You expect your withholding and refundable credits to be
                  less than the smaller of:
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1" color="textSecondary">
                      90% of the tax to be shown on your current year’s tax return, or
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1" color="textSecondary">
                      100% of the tax shown on your prior year’s tax return.
                      (Your prior year tax return must cover all 12 months.)
                    </Typography>
                  </li>
                </ul>
              </li>
            </ol>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion className={classes.panel}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          aria-controls="faq-paymyself-content"
          id="faq-paymyself-header"
        >
          <div className={classes.faqBubble}>
            <Typography component="span" variant="body1">?</Typography>
          </div>
          <Typography variant="h4">What are some ways to pay myself?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <Typography variant="body1" color="textSecondary" className={classes.paragraph}>
              W-2: You treat yourself as an employee and pay yourself wages. You’ll need to have
              a defined role and be actively working in the business. If your LLC has multiple
              members then you’re required to pay all members as salaried employees unless
              you&apos;re the only one who has a management role. In addition, your salary
              must be reasonable and fall within industry standards.
            </Typography>
            <Typography variant="body1" color="textSecondary" className={classes.paragraph}>
              Distributions: You receive distributions of profits from LLC based on the percentage
              owned. You can pay yourself a lump sum at year end or estimate what your profits
              will be and pay yourself a monthly distribution based on the estimation.
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Keep Profits in Business: You have the option to keep all the profits in the business
              and not pay any wages.
            </Typography>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion className={classes.panel}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          aria-controls="faq-becomescorp-content"
          id="faq-becomescorp-header"
        >
          <div className={classes.faqBubble}>
            <Typography component="span" variant="body1">?</Typography>
          </div>
          <Typography variant="h4">What is required to become an S-Corp?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <ul className={classes.list}>
              <li>
                <Typography variant="body1" color="textSecondary">
                  Ownership is restricted to United States citizens or legal residents
                  of the United States
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  The corporation cannot have more than 100 shareholders.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  Only individuals, certain trusts, and estates can be shareholders.
                  May not be partnerships, corporations or non-resident alien shareholders.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  The company must be a domestic company with domestic investors.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  They may only offer one class of stock
                  (with the exception of voting and non-voting stock).
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="textSecondary">
                  Not be an ineligible corporation (i.e. certain financial institutions,
                  insurance companies, and domestic international sales corporations).
                </Typography>
              </li>
            </ul>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default AccountingFaq;
